import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowBack, PlayCircle, Analytics } from '@mui/icons-material';
import { getUpcomingMatchById, getVetoStatsForTeamBySeason } from '../../services/MatchService';
import { getMatchesForAnalysis, AnalysisMatch } from '../../services/AnalysisService';
import { getAllTournamentConfigs, TournamentConfig } from '../../services/TournamentService';
import { getDivisionDisplayName } from '../../utils/divisionUtils';
import { AuthContext } from '../../context/AuthContext';
import { admins, currentSeason, mapMappings } from '../../Constants';
import { mapPhotoMappings } from '../AnalysisPage/mapPhotos';
import Loading from '../../components/Loading';
import TeamLogo from '../../components/TeamLogo';
import { UpcomingMatch } from '../../models/UpcomingMatch';
import { MapStats } from '../../models/MapStats';
import { Team } from '../../models/Team';
import { parseMatchTime } from '../../utils/matchTime';
import { countdownTo } from './PreMatch2Shared';
import styles from './PreMatch2.module.css';
import prep from './PreMatch2Prep.module.css';

const PAGE = 25;
const FORM_MAPS = 8;
const TOP_VETO = 3;

/** 2026-09-14T20:00:00 -> "14/9/26". Short enough for a column, unambiguous about the year. */
const shortDate = (iso: string): string => {
    const d = parseMatchTime(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getDate()}/${d.getMonth() + 1}/${String(d.getFullYear()).slice(-2)}`;
};

const bare = (mapName: string) => mapName.replace('de_', '').toLowerCase();
const label = (mapName: string) => mapMappings.get(`de_${bare(mapName)}`) ?? mapName.replace('de_', '');
const photoFor = (mapName: string) => mapPhotoMappings[`de_${bare(mapName)}`];

const topVeto = (veto: MapStats[], key: 'amountPicked' | 'amountBanned') =>
    veto.filter(v => v[key] > 0).sort((a, b) => b[key] - a[key]).slice(0, TOP_VETO);

/** One playable map, flattened from whichever team's feed it arrived on. */
interface Row {
    gameid: string;
    matchid: string;
    map: string;
    team: Team;
    oppName: string;
    us: number;
    them: number;
    won: boolean;
    time: string;
    season: number;
    division: string;
    hasReplay: boolean;
    /** Every team in this fixture that is also in the upcoming match: two for an indbyrdes
     *  game, which the team filter must therefore match on either id. */
    teamIds: string[];
}

const toRow = (m: AnalysisMatch, team: Team): Row => {
    // Keyed by the id the rows came back under, not the one we asked with: a team that
    // registered twice resolves to its canonical id server-side.
    const isTeam1 = m.team1Id === (m.subjectTeamId ?? team.teamId);
    const us = isTeam1 ? m.team1score : m.team2score;
    const them = isTeam1 ? m.team2score : m.team1score;
    return {
        gameid: m.gameid,
        matchid: m.matchid,
        map: m.mapName,
        team,
        oppName: isTeam1 ? m.team2Name : m.team1Name,
        us,
        them,
        won: us > them,
        time: m.matchTime,
        season: m.season,
        division: m.division,
        hasReplay: m.hasReplay,
        teamIds: [team.teamId],
    };
};

/** What the analysis tool will be handed. It compares one team on one map at a time, so a
 *  selection carries both and every game in it must match. */
interface Selection {
    teamId: string | null;
    map: string | null;
    ids: string[];
}

const FormStrip: React.FC<{ rows: Row[] }> = ({ rows }) => (
    <span className={prep.form}>
        {rows.slice(0, FORM_MAPS).map(r => (
            <span className={`${prep.sq} ${r.won ? prep.sqW : prep.sqL}`} key={r.gameid}>
                {r.won ? 'W' : 'L'}
            </span>
        ))}
    </span>
);

/** This season's veto habits in one line: what they take, and what they throw away. */
const VetoLine: React.FC<{ veto: MapStats[] | undefined }> = ({ veto }) => {
    if (!veto) return <span className={prep.veto} />;
    const picks = topVeto(veto, 'amountPicked');
    const bans = topVeto(veto, 'amountBanned');
    if (picks.length === 0 && bans.length === 0) {
        return (
            <span className={prep.veto}>
                <span className={prep.vetoNone}>ingen picks eller bans i sæson {currentSeason}</span>
            </span>
        );
    }
    return (
        <span className={prep.veto}>
            <span className={prep.vetoKey}>Picker</span>
            {picks.length === 0
                ? <span className={prep.vetoNone}>ingen</span>
                : picks.map(v => (
                    <span className={`${prep.vetoChip} ${prep.vetoPick}`} key={v.mapName}>
                        {label(v.mapName)} ×{v.amountPicked}
                    </span>
                ))}
            <span className={`${prep.vetoKey} ${prep.vetoKeyGap}`}>Banner</span>
            {bans.length === 0
                ? <span className={prep.vetoNone}>ingen</span>
                : bans.map(v => (
                    <span className={`${prep.vetoChip} ${prep.vetoBan}`} key={v.mapName}>
                        {label(v.mapName)} ×{v.amountBanned}
                    </span>
                ))}
        </span>
    );
};

/**
 * /prematch/:matchId/prep — pick the maps you want to study, then watch or analyse them.
 *
 * Analysis compares several games of ONE team on ONE map, so the selection is scoped to both
 * and capped at the same tier limit the analysis tool enforces (2 free, 100 for donators).
 * Ticking a game outside the current scope starts a fresh selection rather than building one
 * the analysis tool would load as an empty list.
 */
const PreMatch2Prep: React.FC = () => {
    const { matchId } = useParams<{ matchId: string }>();
    const { user } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [match, setMatch] = useState<UpcomingMatch | null>(null);
    /** Per team, every map that team has played — what the two overview lines count. */
    const [own, setOwn] = useState<Record<string, Row[]> | null>(null);
    /** The same maps merged, with a fixture the two teams played each other kept once. */
    const [rows, setRows] = useState<Row[] | null>(null);
    const [vetos, setVetos] = useState<Record<string, MapStats[]>>({});
    const [tournaments, setTournaments] = useState<TournamentConfig[]>([]);

    const [team, setTeam] = useState<string>('all');
    /** '' until someone picks: then the list opens on the newest season the two teams played in. */
    const [season, setSeason] = useState<string>('');
    const [map, setMap] = useState<string>('all');
    const [replayOnly, setReplayOnly] = useState(false);
    const [shown, setShown] = useState(PAGE);
    const [sel, setSel] = useState<Selection>({ teamId: null, map: null, ids: [] });

    // The same rule the analysis tool applies, so this page can never hand over a selection it
    // would silently trim on arrival.
    const isAdmin = !!user && admins.includes(user.steamID);
    const isDonator = !!(user?.donator || user?.teamTier || isAdmin);
    const maxGames = isDonator ? 100 : 2;

    useEffect(() => {
        if (!matchId) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await getUpcomingMatchById(matchId);
                if (!cancelled) setMatch(data ?? null);
            } catch {
                if (!cancelled) setMatch(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [matchId]);

    // Qualifier and tournament divisions aren't in the static division map, so their display
    // names have to come from the configs that created them.
    useEffect(() => {
        let cancelled = false;
        getAllTournamentConfigs()
            .then(c => { if (!cancelled) setTournaments(c); })
            .catch(() => { /* standard divisions still resolve from the static map */ });
        return () => { cancelled = true; };
    }, []);

    const loadSide = useCallback(async (t: Team): Promise<Row[]> => {
        // mapName omitted: every map the team has played, each with its gameid. /Match/team
        // cannot be used here — CheckForBO3 collapses a Bo3 into one row with no gameid.
        const maps = await getMatchesForAnalysis(t.teamId, '', []);
        return maps.map(m => toRow(m, t));
    }, []);

    useEffect(() => {
        if (!match) return;
        let cancelled = false;
        (async () => {
            const [a, b] = await Promise.all([loadSide(match.team1), loadSide(match.team2)]);
            if (cancelled) return;
            setOwn({ [match.team1.teamId]: a, [match.team2.teamId]: b });
            // A fixture the two teams played against each other arrives on both feeds. Keep one
            // row, but remember both ids so the row still shows when either team is filtered for.
            const byGame = new Map<string, Row>();
            [...a, ...b].forEach(r => {
                const seen = byGame.get(r.gameid);
                if (seen) seen.teamIds = [...seen.teamIds, ...r.teamIds];
                else byGame.set(r.gameid, r);
            });
            setRows(Array.from(byGame.values()).sort((x, y) => (y.time || '').localeCompare(x.time || '')));
        })();
        return () => { cancelled = true; };
    }, [match, loadSide]);

    useEffect(() => {
        if (!match) return;
        let cancelled = false;
        (async () => {
            const [a, b] = await Promise.all([
                getVetoStatsForTeamBySeason(match.team1.teamId, String(currentSeason)).catch(() => []),
                getVetoStatsForTeamBySeason(match.team2.teamId, String(currentSeason)).catch(() => []),
            ]);
            if (!cancelled) setVetos({ [match.team1.teamId]: a, [match.team2.teamId]: b });
        })();
        return () => { cancelled = true; };
    }, [match]);

    const seasons = useMemo(
        () => Array.from(new Set((rows ?? []).map(r => r.season))).sort((a, b) => b - a),
        [rows]
    );
    // Every season at once is a long list for teams with history, so it opens on the latest one.
    const activeSeason = season || (seasons.length ? String(seasons[0]) : 'all');

    // Only maps played in the chosen season, so the map picker never offers an empty list.
    const maps = useMemo(() => {
        const names = new Set((rows ?? [])
            .filter(r => activeSeason === 'all' || String(r.season) === activeSeason)
            .map(r => bare(r.map)));
        return Array.from(names).sort();
    }, [rows, activeSeason]);
    const activeMap = map !== 'all' && maps.indexOf(map) === -1 ? 'all' : map;

    const filtered = useMemo(() => (rows ?? []).filter(r =>
        (team === 'all' || r.teamIds.indexOf(team) !== -1)
        && (activeSeason === 'all' || String(r.season) === activeSeason)
        && (activeMap === 'all' || bare(r.map) === activeMap)
        && (!replayOnly || r.hasReplay)
    ), [rows, team, activeSeason, activeMap, replayOnly]);

    const pick = (next: string) => { setTeam(next); setShown(PAGE); };

    /** Whether this game can join the selection being built, rather than replacing it. */
    const inScope = (r: Row, s: Selection) =>
        !!s.teamId && r.teamIds.indexOf(s.teamId) !== -1 && s.map === r.map;

    const toggle = (r: Row) => setSel(prev => {
        // Outside the current team+map scope the tick means "analyse that instead": a mixed
        // selection would arrive at a tool that can only load one team on one map.
        if (!inScope(r, prev)) return { teamId: r.teamIds[0], map: r.map, ids: [r.gameid] };
        if (prev.ids.indexOf(r.gameid) !== -1) {
            const ids = prev.ids.filter(id => id !== r.gameid);
            return ids.length ? { ...prev, ids } : { teamId: null, map: null, ids: [] };
        }
        if (prev.ids.length >= maxGames) return prev;
        return { ...prev, ids: [...prev.ids, r.gameid] };
    });

    const analyseHref = useMemo(() => {
        if (!sel.teamId || !sel.map || sel.ids.length === 0) return '';
        // The analysis tool loads one season set at a time and would drop a selected game from
        // outside it, so the seasons behind the picks travel with them. The map is required:
        // without it the tool sits on "pick a map above" with nothing loaded.
        const seasons = Array.from(new Set(
            (rows ?? []).filter(r => sel.ids.indexOf(r.gameid) !== -1).map(r => r.season)
        )).sort((a, b) => a - b);
        return `/analyse?team=${sel.teamId}&map=${sel.map}`
            + `&games=${sel.ids.join(',')}&seasons=${seasons.join(',')}`;
    }, [sel, rows]);

    if (loading) {
        return <div className="container text-center text-light"><Loading /></div>;
    }
    if (!match) {
        return (
            <div className={styles.notice}>
                <h2>Kampen findes ikke</h2>
                <p>Der er ingen kommende kamp med det id. <Link to="/matches">Se alle kampe</Link>.</p>
            </div>
        );
    }

    const kickoff = parseMatchTime(match.match_time);
    const sides = [match.team1, match.team2];
    const atCap = sel.ids.length >= maxGames;
    const selectedTeam = sides.find(t => t.teamId === sel.teamId);

    return (
        <div className={prep.page}>
            <div className={prep.ctx}>
                <Link to={`/prematch/${matchId}`} className={prep.back}>
                    <ArrowBack sx={{ fontSize: 15 }} />
                    {match.team1.teamName} vs {match.team2.teamName}
                </Link>
                <h1 className={prep.title}>Forbered kampen</h1>
                <span className={prep.kick}>
                    {kickoff.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })} · {countdownTo(kickoff)}
                </span>
            </div>

            <div className={styles.panel}>
                <div className={prep.teams}>
                    {sides.map(t => {
                        const mine = own?.[t.teamId] ?? [];
                        const withReplay = mine.filter(r => r.hasReplay).length;
                        return (
                            <div className={prep.team} key={t.teamId}>
                                <TeamLogo logo={t.teamLogo} name={t.teamName} className={prep.teamCrest} />
                                <Link to={`/team/${t.teamId}`} className={prep.teamName}>{t.teamName}</Link>
                                <span className={prep.teamCount}>
                                    {own === null ? '…' : `${mine.length} maps · ${withReplay} med replay`}
                                </span>
                                <FormStrip rows={mine} />
                                <VetoLine veto={vetos[t.teamId]} />
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className={styles.panel} style={{ marginTop: 14 }}>
                <div className={prep.filters}>
                    <span className={prep.fkey}>Hold</span>
                    <button
                        type="button"
                        className={`${prep.chip} ${team === 'all' ? prep.chipOn : ''}`}
                        onClick={() => pick('all')}
                    >
                        Begge
                    </button>
                    {sides.map(t => (
                        <button
                            type="button"
                            className={`${prep.chip} ${team === t.teamId ? prep.chipOn : ''}`}
                            onClick={() => pick(t.teamId)}
                            key={t.teamId}
                        >
                            <TeamLogo logo={t.teamLogo} name={t.teamName} className={prep.chipCrest} />
                            {t.teamName}
                        </button>
                    ))}

                    <label className={prep.fkey} htmlFor="prep-season" style={{ marginLeft: 10 }}>Sæson</label>
                    <select
                        id="prep-season"
                        className={prep.select}
                        value={activeSeason}
                        onChange={e => { setSeason(e.target.value); setShown(PAGE); }}
                    >
                        {seasons.map(s => <option value={String(s)} key={s}>Sæson {s}</option>)}
                        <option value="all">Alle sæsoner</option>
                    </select>

                    <label className={prep.fkey} htmlFor="prep-map" style={{ marginLeft: 10 }}>Map</label>
                    <select
                        id="prep-map"
                        className={prep.select}
                        value={activeMap}
                        onChange={e => { setMap(e.target.value); setShown(PAGE); }}
                    >
                        <option value="all">Alle maps</option>
                        {maps.map(m => <option value={m} key={m}>{label(m)}</option>)}
                    </select>

                    <button
                        type="button"
                        className={`${prep.chip} ${replayOnly ? prep.chipOn : ''}`}
                        onClick={() => { setReplayOnly(v => !v); setShown(PAGE); }}
                    >
                        <PlayCircle sx={{ fontSize: 15 }} />Kun med replay
                    </button>

                    <span className={prep.count}>
                        {rows === null ? '' : `${filtered.length} maps`}
                    </span>
                </div>

                {rows === null ? (
                    <div className="text-center text-light" style={{ padding: 30 }}><Loading /></div>
                ) : filtered.length === 0 ? (
                    <div className={prep.empty}>Ingen maps matcher filtrene.</div>
                ) : (
                    <>
                        {filtered.slice(0, shown).map(r => {
                            const photo = photoFor(r.map);
                            const m = bare(r.map);
                            const checked = sel.ids.indexOf(r.gameid) !== -1;
                            // No stored rounds means nothing for the analysis tool to read — the
                            // same rule it applies to its own list. The cap only blocks games
                            // that would join the current selection; one outside its team+map
                            // scope starts over, so it stays available.
                            const selectable = r.hasReplay && (checked || !atCap || !inScope(r, sel));
                            return (
                                <div className={`${prep.row} ${checked ? prep.rowOn : ''}`} key={r.gameid}>
                                    <input
                                        type="checkbox"
                                        className={prep.check}
                                        checked={checked}
                                        disabled={!selectable}
                                        onChange={() => toggle(r)}
                                        aria-label={`Vælg ${label(r.map)} mod ${r.oppName} til analyse`}
                                        title={r.hasReplay
                                            ? 'Vælg til analyse'
                                            : 'Ingen replay gemt — kan ikke analyseres'}
                                    />
                                    <span
                                        className={prep.thumb}
                                        style={photo ? { backgroundImage: `url(${photo})` } : undefined}
                                    />
                                    <span className={`${prep.wl} ${r.won ? prep.win : prep.loss}`}>
                                        {r.won ? 'W' : 'L'}
                                    </span>
                                    <span>
                                        <span className={prep.headline}>
                                            <b>{label(r.map)}</b>{' '}
                                            <span className={prep.score}>{r.us}–{r.them}</span>{' '}
                                            <span className={prep.opp}>mod {r.oppName}</span>
                                        </span>
                                        <span className={prep.sub}>
                                            <TeamLogo
                                                logo={r.team.teamLogo}
                                                name={r.team.teamName}
                                                className={prep.subCrest}
                                            />
                                            {r.team.teamName} · Sæson {r.season} ·{' '}
                                            {getDivisionDisplayName(r.division, tournaments)}
                                            {r.teamIds.length > 1 && (
                                                <span className={prep.h2h}>indbyrdes</span>
                                            )}
                                        </span>
                                    </span>
                                    <span className={prep.when}>{shortDate(r.time)}</span>
                                    <span className={prep.actions}>
                                        {r.hasReplay ? (
                                            <Link
                                                to={`/match/${r.matchid}?tab=replay&map=${m}`}
                                                className={prep.action}
                                            >
                                                <PlayCircle sx={{ fontSize: 14 }} />Replay
                                            </Link>
                                        ) : (
                                            <span
                                                className={`${prep.action} ${prep.actionOff}`}
                                                title="Ingen replay gemt for dette map"
                                            >
                                                <PlayCircle sx={{ fontSize: 14 }} />Replay
                                            </span>
                                        )}
                                    </span>
                                </div>
                            );
                        })}
                        {shown < filtered.length && (
                            <button
                                type="button"
                                className={prep.more}
                                onClick={() => setShown(s => s + PAGE)}
                            >
                                Vis {Math.min(PAGE, filtered.length - shown)} mere
                            </button>
                        )}
                    </>
                )}

            </div>

            {sel.ids.length > 0 && (
                <>
                    <div className={prep.barSpacer} />
                    <div className={prep.bar}>
                      <div className={prep.barInner}>
                        <span className={prep.barCount}>
                            <b>{sel.ids.length}</b> af {maxGames} valgt
                            {selectedTeam && (
                                <span className={prep.barTeam}>
                                    {' '}· {selectedTeam.teamName} · {label(sel.map ?? '')}
                                </span>
                            )}
                        </span>
                        {!isDonator && (
                            <span className={prep.barNote}>
                                {atCap ? 'Gratis-grænsen er 2 kampe. ' : ''}
                                <Link to="/premium">Donatorer kan sammenligne 100 →</Link>
                            </span>
                        )}
                        <button
                            type="button"
                            className={prep.barClear}
                            onClick={() => setSel({ teamId: null, map: null, ids: [] })}
                        >
                            Ryd
                        </button>
                        <Link to={analyseHref} className={prep.barGo}>
                            <Analytics sx={{ fontSize: 16 }} />
                            Analysér {sel.ids.length} {sel.ids.length === 1 ? 'kamp' : 'kampe'}
                        </Link>
                      </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PreMatch2Prep;
